/**
 * Validate font coverage for text elements when a design is loaded.
 *
 * Older designs may contain characters that aren't actually present in their chosen
 * font (browser glyph substitution masked the issue at the time). Strip those
 * characters now so the print-ready PDF matches what the customer sees.
 *
 * Runs once per loaded design ID.
 */

import { useEffect, useRef } from 'react'
import type { TextElement } from '../../domain/canvas/entities/element'
import { fontLoader } from '../../infrastructure/services/fontLoaderService'
import { filterUnsupportedChars } from '../../domain/canvas/services/fontGlyphFilter'
import { useQueryFonts } from './useQueryFonts'
import { useCanvasStore } from '../../stores/canvasStore'
import { useDesignStore } from '../../stores/designStore'
import { useToast } from '../../hooks/useToast'
import { useIntlayer } from 'react-intlayer'

const resolveIntlString = (value: unknown): string => {
  if (typeof value === 'string') return value
  if (value && typeof (value as { value?: unknown }).value === 'string') {
    return (value as { value: string }).value
  }
  return String(value ?? '')
}

export const useValidateLoadedDesignFonts = (): void => {
  const fonts = useQueryFonts()
  const elements = useCanvasStore((state) => state.elements)
  const updateTextContent = useCanvasStore((state) => state.updateTextContent)
  const currentDesign = useDesignStore((state) => state.currentDesign)
  const { showToast } = useToast()
  const intl = useIntlayer('designStudioEditor')

  // Track which design IDs we've already validated so we don't re-run on every element edit.
  const validatedRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    const designId = currentDesign?.designId
    if (!designId) return
    if (!fonts || fonts.length === 0) return
    if (validatedRef.current.has(designId)) return

    const textElements = Object.values(elements).filter(
      (el): el is TextElement => el.type === 'text',
    )
    if (textElements.length === 0) return

    // Mark up-front so we don't re-enter while async work is in flight.
    validatedRef.current.add(designId)

    const run = async () => {
      const allRemoved = new Set<string>()

      for (const element of textElements) {
        const fontMeta = fonts.find(
          (f) =>
            f.name === element.fontFamily &&
            f.weight === element.fontWeight &&
            f.italic === element.fontItalic,
        )
        // Without metadata we cannot fetch/parse the font; skip silently (e.g. system fonts).
        if (!fontMeta) continue

        try {
          await fontLoader.loadFont(fontMeta.name, fontMeta.url, fontMeta.weight, fontMeta.italic)
        } catch {
          continue
        }

        const supported = fontLoader.getSupportedCodepoints(element.fontFamily)
        if (!supported) continue

        const { filtered, removed } = filterUnsupportedChars(element.content, supported)
        if (filtered === element.content) continue

        for (const char of removed) allRemoved.add(char)
        try {
          await updateTextContent(element.id, filtered)
        } catch {
          // Best-effort: don't block the toast if a single update fails.
        }
      }

      if (allRemoved.size > 0) {
        const template = resolveIntlString(intl.alerts.unsupportedCharactersOnLoad)
        const message = template.replace('{chars}', Array.from(allRemoved).join(' '))
        showToast(message, 'error')
      }
    }

    void run()
  }, [currentDesign?.designId, fonts, elements, updateTextContent, showToast, intl])
}
